import { reaction } from 'mobx';

import { ModalsManager } from '@/components/modals/modalsManager';
import { CreateRoutineModal } from '@/features/routines/routines-create';
import { startRoutineSetup } from '@/features/routines/start-routine-setup';
import { logError } from '@/lib/errorLogging';
import { logger } from '@/lib/logger';

import { PluginDialogShell } from './plugin-dialog';
import { pluginsStore } from './plugins.store';

import type {
  DialogHost,
  PluginDialogProps,
  PluginMessageHandler,
  PluginMessageName,
  PluginMessages,
  RoutineSetupHost,
} from './define-plugin';
import type { ComponentType } from 'react';

type Capabilities = DialogHost & RoutineSetupHost;

const DIALOG_TITLES: Record<PluginMessageName, string> = {
  'routine.create': 'New routine',
};

// Each message's handler gets the capabilities listed for it in `PluginMessages`; `routine.create` has them all
const HOSTS: {
  [Name in PluginMessageName]: (
    capabilities: Capabilities
  ) => PluginMessages[Name]['host'];
} = {
  'routine.create': (capabilities) => capabilities,
};

/**
 * One handler call's host. It keeps at most one dialog, closes it when the plugin is turned off, and does
 * nothing once the plugin is off or the call is over. The call is over when the person closes its dialog,
 * the request reaches a new chat, or core's routine form opens
 */
const createHost = (pluginId: string, name: PluginMessageName) => {
  // What had focus when the call began, such as New routine. It gets focus back from every modal the call
  // shows, so a handoff from one modal to the next does not leave focus on the page
  const opener = document.activeElement;
  let dialog: { hide: () => void } | undefined;
  // The first setup the call accepted; a second click joins it instead of starting another chat
  let setup: Promise<void> | undefined;
  let isDisposed = false;
  const isLive = () => !isDisposed && pluginsStore.isEnabled(pluginId);
  const closeDialog = () => {
    const closing = dialog;
    dialog = undefined;
    closing?.hide();
  };
  const dispose = () => {
    isDisposed = true;
    stopWatching();
    closeDialog();
  };
  // Turning the plugin off ends this call for good, even if the plugin comes back on
  const stopWatching = reaction(
    () => pluginsStore.isEnabled(pluginId),
    (isEnabled) => !isEnabled && dispose(),
    { name: `plugin host: ${pluginId} ${name}` }
  );

  const capabilities: Capabilities = {
    openDialog: (component, props, options) => {
      if (!isLive()) return;
      closeDialog();
      const shown = ModalsManager.show(
        PluginDialogShell,
        {
          title: DIALOG_TITLES[name],
          component: component as ComponentType<PluginDialogProps>,
          props,
          size: options?.size ?? 'default',
        },
        {
          opener,
          // Closed by the person, the command bar or a navigation, it ends the call; a dialog this call
          // replaced or closed itself is no longer its own
          onClosed: () => dialog === shown && dispose(),
        }
      );
      dialog = shown;
    },
    startRoutineSetup: (request) => {
      if (setup) return setup;
      if (!isLive()) return Promise.resolve();
      // Once core has the request, the send finishes even if the plugin is turned off meanwhile. A failed
      // start leaves the dialog open to try again
      setup = startRoutineSetup(request).then(dispose, (error: unknown) => {
        setup = undefined;
        throw error;
      });
      return setup;
    },
    openRoutineForm: () => {
      if (!isLive()) return;
      dispose();
      ModalsManager.show(CreateRoutineModal, { isCustom: true }, { opener });
    },
  };

  // A handler that took the message without a dialog or a setup has nothing left for its host to do
  const settle = () => {
    if (!dialog && !setup) dispose();
  };

  return { capabilities, dispose, settle };
};

const runHandler = <Name extends PluginMessageName>(
  pluginId: string,
  name: Name,
  handle: PluginMessageHandler<Name>,
  message: PluginMessages[Name]['message']
) => {
  const host = createHost(pluginId, name);
  try {
    if (handle(message, HOSTS[name](host.capabilities))) {
      host.settle();
      return true;
    }
  } catch (error) {
    logError(error, `Plugin ${pluginId} failed to handle ${name}`, {
      context: 'plugin-messages',
      metadata: { pluginId, message: name },
    });
  }
  host.dispose();
  return false;
};

const dispatch = <Name extends PluginMessageName>(
  name: Name,
  message: PluginMessages[Name]['message'],
  fallback: () => void
) => {
  const handlers = pluginsStore.handlersOf(name);
  if (handlers.length > 1)
    logger.warn(
      `Plugins ${handlers.map(({ id }) => id).join(' and ')} both handle ${name}, so core handles it`
    );
  const [only] = handlers;
  const isHandled =
    handlers.length === 1 && runHandler(only.id, name, only.handle, message);
  if (!isHandled) fallback();
};

/**
 * Tells plugins a core moment happened. One enabled plugin that handles it takes it. When none does, two
 * do, or the handler declines or throws, `fallback` runs what core does without plugins, once. While an
 * enabled plugin is still loading, it waits, reading the flags again whenever the plugins loading change,
 * so the handlers are counted with every enabled plugin in place. The returned function drops a message
 * still waiting, for a screen that closes before it is handled
 */
export const sendPluginMessage = <Name extends PluginMessageName>(
  name: Name,
  message: PluginMessages[Name]['message'],
  fallback: () => void
) => {
  const dropped = new AbortController();
  const send = () => {
    if (dropped.signal.aborted) return;
    const loading = pluginsStore.whenLoadingChanges(dropped.signal);
    if (loading) void loading.then(send);
    else dispatch(name, message, fallback);
  };
  send();
  return () => dropped.abort();
};
